import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, MapPin, Clock, Send, CheckCircle2, AlertCircle, Mail, MessageCircle, ArrowUpRight } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { formatPhone } from "../lib/formatters";
import KineticHeading from "../components/KineticHeading";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WA_PHONE = "18196490166";

const initial = { name: "", phone: "", email: "", project: "", message: "" };

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k) => (e) => {
    const v = k === "phone" ? formatPhone(e.target.value) : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      setStatus({ state: "error", msg: t("contact.missing") });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ state: "success", msg: t("contact.success") });
      setForm(initial);
    } catch (err) {
      setStatus({ state: "error", msg: t("contact.error") });
    }
  };

  const waHref = `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(t("wa.message"))}`;

  return (
    <main data-testid="contact-page" className="bg-paper">

      {/* HERO */}
      <section className="relative bg-ink text-white overflow-hidden">
        <div className="absolute inset-0 bg-blueprint opacity-50" />
        <div className="absolute inset-0 bg-grain-dark opacity-60" />
        <div
          className="absolute inset-0 opacity-70"
          style={{ background: "radial-gradient(60% 55% at 78% 30%, rgba(232,123,58,0.28) 0%, rgba(232,123,58,0) 60%)" }}
        />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-12 pt-24 pb-28 md:pt-32 md:pb-40">
          <div className="chapter-label text-[#E87B3A]">Chapitre 04 — {t("contact.kicker")}</div>
          <h1 className="mt-8 font-display uppercase text-white text-6xl md:text-8xl lg:text-[9vw] leading-[0.88]">
            <KineticHeading lines={[t("contact.title1")]} />
            <KineticHeading
              lines={[t("contact.title2")]}
              delay={0.15}
              lineClassName="font-serif italic text-[#E87B3A] tracking-tight"
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="mt-8 font-body text-white/70 text-lg max-w-2xl leading-relaxed"
          >
            {t("contact.sub")}
          </motion.p>

          {/* Quick contact chips */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            <a
              href="tel:8193503027"
              data-testid="hero-call"
              className="group inline-flex items-center gap-3 bg-[#E87B3A] hover:bg-[#F08A47] text-white pl-5 pr-3 py-3 rounded-sm font-body font-bold text-xs uppercase tracking-[0.2em]"
            >
              <Phone size={14} /> 819-350-3027
              <span className="w-7 h-7 flex items-center justify-center bg-white/15 group-hover:translate-x-0.5 transition-transform"><ArrowUpRight size={13} /></span>
            </a>
            <a
              href={waHref}
              target="_blank" rel="noopener noreferrer"
              data-testid="hero-wa"
              className="group inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1EBE5B] text-white pl-5 pr-3 py-3 rounded-sm font-body font-bold text-xs uppercase tracking-[0.2em]"
            >
              <MessageCircle size={14} /> {t("wa.label")}
              <span className="w-7 h-7 flex items-center justify-center bg-white/15 group-hover:translate-x-0.5 transition-transform"><ArrowUpRight size={13} /></span>
            </a>
            <a
              href="mailto:info@locationrnc.ca"
              className="group inline-flex items-center gap-3 border border-white/25 hover:border-white text-white px-5 py-3 rounded-sm font-body font-bold text-xs uppercase tracking-[0.2em]"
            >
              <Mail size={14} /> info@locationrnc.ca
            </a>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 lg:px-12 py-24 md:py-32 grid grid-cols-12 gap-10">
        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 lg:col-span-7"
        >
          <div className="chapter-label text-[#1A4E72]">01 — Formulaire</div>
          <h2 className="mt-6 font-display text-5xl md:text-6xl text-[#0F172A] uppercase leading-[0.95]">
            {t("contact.formTitle")}
          </h2>
          <p className="mt-4 font-body text-[#475569]">
            {t("contact.formIntro")}{" "}
            <a href="tel:8193503027" className="text-[#E87B3A] font-bold underline decoration-[#E87B3A]/40 underline-offset-4">
              819-350-3027
            </a>.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <FormField label={t("contact.nameLabel")} required>
                <input
                  type="text" required
                  placeholder="Jean Tremblay"
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-name"
                  className="editorial-input"
                />
              </FormField>
              <FormField label={t("contact.phoneLabel")} required>
                <input
                  type="tel" required
                  placeholder="555-555-5555"
                  maxLength={12}
                  inputMode="numeric"
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="contact-phone-input"
                  className="editorial-input"
                />
              </FormField>
            </div>

            <FormField label={t("contact.emailLabel")}>
              <input
                type="email"
                placeholder="jean@exemple.ca"
                value={form.email}
                onChange={update("email")}
                data-testid="contact-email"
                className="editorial-input"
              />
            </FormField>

            <FormField label={t("contact.projectLabel")}>
              <input
                type="text"
                value={form.project}
                onChange={update("project")}
                placeholder={t("contact.projectPh")}
                data-testid="contact-project"
                className="editorial-input"
              />
            </FormField>

            <FormField label={t("contact.msgLabel")} required>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={update("message")}
                placeholder={t("contact.msgPh")}
                data-testid="contact-message"
                className="editorial-input resize-none"
              />
            </FormField>

            {status.state === "success" && (
              <div className="flex items-start gap-3 p-5 bg-[#3A6D3B] text-white rounded-sm shadow-lg" data-testid="contact-success">
                <CheckCircle2 size={24} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-display text-lg uppercase leading-tight">Message envoyé ✓</div>
                  <div className="font-body text-sm mt-1 text-white/90">{status.msg}</div>
                </div>
              </div>
            )}
            {status.state === "error" && (
              <div className="flex items-start gap-3 p-5 bg-red-50 border-2 border-red-300 text-red-700 rounded-sm" data-testid="contact-error">
                <AlertCircle size={22} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-display text-lg uppercase leading-tight">Erreur</div>
                  <div className="font-body text-sm mt-1">{status.msg}</div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={status.state === "loading"}
              data-testid="contact-submit"
              className="group inline-flex items-center gap-3 bg-[#E87B3A] hover:bg-[#F08A47] disabled:opacity-60 disabled:cursor-not-allowed text-white pl-8 pr-4 py-4 rounded-sm font-body font-bold uppercase tracking-[0.2em] text-xs transition-all shadow-md hover:shadow-lg hover:-translate-y-[1px]"
            >
              {status.state === "loading" ? (
                <>
                  <span className="animate-pulse">{t("contact.submitting")}</span>
                  <span className="w-9 h-9 flex items-center justify-center bg-white/15">
                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </span>
                </>
              ) : (
                <>
                  {t("contact.submit")}
                  <span className="w-9 h-9 flex items-center justify-center bg-[#0F172A] group-hover:translate-x-1 transition-all">
                    <Send size={14} />
                  </span>
                </>
              )}
            </button>
          </form>

          <style>{`
            .editorial-input {
              display: block; width: 100%;
              padding: 14px 16px;
              background: #FFFFFF;
              border: 1.5px solid rgba(15,23,42,0.18);
              border-radius: 6px;
              font-family: 'Manrope', sans-serif; font-size: 15px;
              color: #0F172A;
              transition: border-color .2s ease, box-shadow .2s ease, background .2s ease;
              outline: none;
              box-shadow: 0 1px 2px rgba(15,23,42,0.03);
            }
            .editorial-input::placeholder { color: rgba(15,23,42,0.35); }
            .editorial-input:hover { border-color: rgba(15,23,42,0.35); }
            .editorial-input:focus {
              border-color: #E87B3A;
              background: #FFFDFB;
              box-shadow: 0 0 0 4px rgba(232,123,58,0.12);
            }
          `}</style>
        </motion.div>

        {/* Info column */}
        <motion.aside
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="col-span-12 lg:col-span-5"
        >
          <div className="bg-[#0B1620] text-white p-8 md:p-10 rounded-sm relative overflow-hidden">
            <div className="absolute inset-0 bg-blueprint-dense opacity-40" />
            <div className="relative">
              <div className="chapter-label text-[#E87B3A]">02 — Direct</div>
              <h3 className="mt-6 font-display text-4xl md:text-5xl uppercase leading-[0.95]">
                {t("contact.sideTitle")}
              </h3>

              <div className="mt-10 space-y-6">
                <a href="tel:8193503027" className="group flex items-start gap-4" data-testid="contact-info-phone">
                  <div className="w-11 h-11 bg-[#E87B3A] flex items-center justify-center rounded-sm shrink-0">
                    <Phone size={18} />
                  </div>
                  <div>
                    <div className="font-body text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">{t("contact.phone")}</div>
                    <div className="font-display text-2xl mt-1 group-hover:text-[#E87B3A] transition-colors">
                      Phil — 819-350-3027
                    </div>
                  </div>
                </a>

                <a
                  href={waHref}
                  target="_blank" rel="noopener noreferrer"
                  className="group flex items-start gap-4"
                  data-testid="contact-info-wa"
                >
                  <div className="w-11 h-11 bg-[#25D366] flex items-center justify-center rounded-sm shrink-0">
                    <MessageCircle size={18} />
                  </div>
                  <div>
                    <div className="font-body text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">WhatsApp</div>
                    <div className="font-display text-2xl mt-1 group-hover:text-[#25D366] transition-colors">
                      {t("wa.label")}
                    </div>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 bg-[#1A4E72] flex items-center justify-center rounded-sm shrink-0">
                    <Mail size={18} />
                  </div>
                  <div>
                    <div className="font-body text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">Courriel</div>
                    <a href="mailto:info@locationrnc.ca" className="font-body text-base mt-1 block hover:text-[#E87B3A] transition-colors" data-testid="contact-info-email">
                      info@locationrnc.ca
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 bg-[#3A6D3B] flex items-center justify-center rounded-sm shrink-0">
                    <MapPin size={18} />
                  </div>
                  <div>
                    <div className="font-body text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">{t("contact.area")}</div>
                    <div className="font-body text-base mt-1">Région du Nord, Québec</div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 border border-white/20 flex items-center justify-center rounded-sm shrink-0">
                    <Clock size={18} />
                  </div>
                  <div>
                    <div className="font-body text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">{t("contact.hours")}</div>
                    <div className="font-body text-base mt-1 leading-relaxed" style={{ whiteSpace: "pre-line" }}>
                      Lun – Sam : 7h – 18h{"\n"}Dim : sur rendez-vous
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-white/10">
                <div className="font-body text-[10px] text-white/50 uppercase tracking-[0.3em] mb-3">{t("contact.pricing")}</div>
                <p className="font-body text-sm text-white/80 leading-relaxed">
                  {t("contact.pricingBody")}
                </p>
              </div>
            </div>
          </div>
        </motion.aside>
      </section>
    </main>
  );
}

const FormField = ({ label, required, children }) => (
  <div>
    <label className="font-body text-[11px] font-bold uppercase tracking-[0.22em] text-[#0F172A] block mb-2">
      {label}{required && <span className="text-[#E87B3A] ml-1">*</span>}
    </label>
    {children}
  </div>
);
